import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, pluck } from 'rxjs';
import { Injectable } from '@angular/core';

import { IContractLot, IGenericResult, ILot, IResponse } from './../../models/generic';
import { environment } from 'src/environments/environment';
import {
  IBeneficiaryEconomicGroup,
  IContractIds,
  IEconomicGroupUnit,
  IManualBatchContractFilter,
  IManualBatchLiberacao
} from 'src/app/models/manual-batch';
import { SHOW_LOADER } from '../loader-interceptor.service';

type BehaviorContracts = {
  contracts: IContractLot[];
  contractIds: string[];
};

@Injectable({
  providedIn: 'root'
})
export class ApiManualBatchService {
  //baseUrlBatchTransfer: string = `${environment.apis.transfers.baseUrl}/${environment.apis.transfers.microservices.batch}`;
  baseUrlBatchTransfer: string = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.transferBatch}`;
  //baseUrlManualBatchTransfer: string = `${environment.apis.transfers.baseUrl}/${environment.apis.transfers.microservices.batch}/manual-batch`;
  baseUrlManualBatchTransfer: string = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.transferBatch}/manual-batch`;
  /*baseUrlEconomicGroup: string = `${environment.apis.transfers.baseUrl}/${environment.apis.transfers.microservices.economicGroup}`;*/
  baseUrlEconomicGroup: string = `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.transferEconomicGroup}`;

  context = new HttpContext().set(SHOW_LOADER, false);

  selectedContracts = new BehaviorSubject<BehaviorContracts>({
    contracts: [],
    contractIds: []
  });

  exceptionsContracts = new BehaviorSubject<BehaviorContracts>({
    contracts: [],
    contractIds: []
  });

  constructor(private _http: HttpClient) { }

  changeAllSelectedContracts(): void {
    this.selectedContracts.next({ contracts: [], contractIds: ['all'] });
  }

  changeSelectedContracts(changedContract: IContractLot): void {
    if (
      changedContract.Beneficiaries &&
      changedContract.Beneficiaries?.length > 1
    ) {
      this.changeSelectedContractsMultipleBeneficiaries(changedContract);
    } else {
      this.changeSelectedContractsDefault(changedContract);
    }
  }

  changeSelectedContractsDefault(changedContract: IContractLot): void {
    const behaviorContracts = this.changeContracts(
      changedContract,
      this.selectedContracts.value
    );
    this.selectedContracts.next(behaviorContracts);
  }

  changeSelectedContractsMultipleBeneficiaries(
    changedContract: IContractLot
  ): void {
    const behaviorContracts = this.changeContractsMultipleBeneficiaries(
      changedContract,
      this.selectedContracts.value
    );
    this.selectedContracts.next(behaviorContracts);
  }

  changeExceptionsContracts(changedContract: IContractLot): void {
    if (
      changedContract.Beneficiaries &&
      changedContract.Beneficiaries?.length > 1
    ) {
      this.changeExceptionsContractsMultipleBeneficiaries(changedContract);
    } else {
      this.changeExceptionsContractsDefault(changedContract);
    }
  }

  changeExceptionsContractsDefault(changedContract: IContractLot): void {
    const behaviorContracts = this.changeContracts(
      changedContract,
      this.exceptionsContracts.value
    );
    this.exceptionsContracts.next(behaviorContracts);
  }

  changeExceptionsContractsMultipleBeneficiaries(
    changedContract: IContractLot
  ): void {
    const behaviorContracts = this.changeContractsMultipleBeneficiaries(
      changedContract,
      this.exceptionsContracts.value
    );
    this.exceptionsContracts.next(behaviorContracts);
  }

  changeContracts(
    changedContract: IContractLot,
    contractsToCompare: BehaviorContracts
  ): BehaviorContracts {
    const { contracts, contractIds } = contractsToCompare;

    const contractIndex = contracts.findIndex(
      (ctt) => ctt.ContractId === changedContract.ContractId
    );

    const contractIdIndex = contractIds.findIndex(
      (id) => id === changedContract.ContractId
    );

    if (contractIndex < 0) {
      contracts.push(changedContract);
      contractIds.push(changedContract.ContractId);
    } else {
      contracts.splice(contractIndex, 1);
      contractIds.splice(contractIdIndex, 1);
    }

    return { contracts, contractIds };
  }

  changeContractsMultipleBeneficiaries(
    changedContract: IContractLot,
    contractsToCompare: BehaviorContracts
  ): BehaviorContracts {
    const { contracts, contractIds } = contractsToCompare;

    const index = contracts.findIndex(
      (ctt) => ctt.ContractId === changedContract.ContractId
    );

    if (index < 0) {
      contracts.push(changedContract);
      contractIds.push(...changedContract.ContractIds!);
    } else {
      contracts.splice(index, 1);
      changedContract.ContractIds?.forEach((contractId) => {
        let index = contractIds.findIndex((cttId) => cttId === contractId);

        if (index >= 0) {
          contractIds.splice(index, 1);
        }
      });
    }

    return { contracts, contractIds };
  }

  checkUncheckContract(contractId: string, checkAll: boolean): boolean {
    const selectedContractIds = this.selectedContracts.value.contractIds;
    const exceptionsContractIds = this.exceptionsContracts.value.contractIds;

    return (
      selectedContractIds.includes(contractId) ||
      (checkAll && !exceptionsContractIds.includes(contractId))
    );
  }

  resetContracts(): void {
    this.selectedContracts.next({ contracts: [], contractIds: [] });
    this.exceptionsContracts.next({ contracts: [], contractIds: [] });
  }

  createManualBatch(): Observable<ILot> {
    return this._http.post<ILot>(this.baseUrlManualBatchTransfer, {});
  }

  addContractsManualBatch(contractIds: IContractIds): Observable<ILot> {
    return this._http.post<ILot>(
      `${this.baseUrlManualBatchTransfer}/add-contracts`,
      contractIds
    );
  }

  deleteContractsManualBatch(
    contractIds: IContractIds
  ): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrlManualBatchTransfer}/delete-contracts`,
      contractIds,
      {
        headers: new HttpHeaders({
          'X-Skip-Error-Handler': 'true'
        })
      }
    );
  }

  getContractsManualBatch(
    filter: IManualBatchContractFilter
  ): Observable<IGenericResult<IContractLot[]>> {
    return this._http.post<IGenericResult<IContractLot[]>>(
      `${this.baseUrlManualBatchTransfer}/contracts`,
      filter
    );
  }

  integrateManualTransferWithSap(
    batchId: string
  ): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrlManualBatchTransfer}/sapintegration/${batchId}`,
      {}
    );
  }

  liberarManualTransferBatch(
    batchId: string,
    payload: IManualBatchLiberacao
  ): Observable<IGenericResult<IManualBatchLiberacao>> {
    
    return this._http.post<IGenericResult<IManualBatchLiberacao>>(
      `${this.baseUrlManualBatchTransfer}/liberar-manual/${batchId}/batchInfo`,
      payload
    );
  }

  getAuthorizationFile(batchId: string): Observable<any> {
    return this._http.get(
      `${this.baseUrlEconomicGroup}/authorization-file/${batchId}`,
      {
        responseType: 'blob',
        context: this.context
      }
    );
  }

  postAuthorizationFile(id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post(
      `${this.baseUrlEconomicGroup}/authorization-file/${id}`,
      formData
    );
  }

  getEconomicGroupUnits(
    beneficiaryId: string
  ): Observable<IEconomicGroupUnit[]> {
    return this._http
      .get<IGenericResult<any>>(
        `${this.baseUrlEconomicGroup}/economic-group/${beneficiaryId}`
      )
      .pipe(pluck('Data', 'Unidades'));
  }

  getEconomicGroupPartner(
    beneficiaryId: string
  ): Observable<IEconomicGroupUnit> {
    return this._http
      .get<IGenericResult<any>>(
        `${this.baseUrlEconomicGroup}/partner/${beneficiaryId}`
      )
      .pipe(pluck('Data'));
  }

  updateBeneficiaryEconomicGroup(
    batchId: string,
    beneficiary: IBeneficiaryEconomicGroup
  ): Observable<IGenericResult<IBeneficiaryEconomicGroup>> {
    return this._http.post<IGenericResult<IBeneficiaryEconomicGroup>>(
      `${this.baseUrlEconomicGroup}/batch/${batchId}/set-partner`,
      beneficiary
    );
  }

  saveEstimatedPaymentDate(
    batchId: string,
    PaymentDate: {
      EstimatedPaymentDate: string;
    }
  ): Observable<IGenericResult<unknown>> {
    return this._http.post<IGenericResult<unknown>>(
      `${this.baseUrlBatchTransfer}/${batchId}/update-paymentdate`,
      PaymentDate
    );
  }

  cancelManualBatch(lotId: string): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrlManualBatchTransfer}/cancel/${lotId}`, {}
    );
  }

  cancelManualBatchPosIntegracao(
    lotId: string,
    payload: { Justificativa: string }
  ): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrlManualBatchTransfer}/cancel-pos-integracao/${lotId}`,
      payload
    );
  }

  getCancelamentoStatus(lotId: string): Observable<IGenericResult<string | null>> {
    return this._http.get<IGenericResult<string | null>>(
      `${this.baseUrlManualBatchTransfer}/${lotId}/cancelamento-status`
    );
  }


  returnStatusManualBatch(lotId: string): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrlManualBatchTransfer}/return-status/${lotId}`, {}
    );
  }

  validateContracts(contracts: IContractLot[]): Observable<IResponse> {
    return this._http.post<IResponse>(
      `${this.baseUrlManualBatchTransfer}/validate-contracts`, contracts
    );
  }

  selectContractById(contractId: string): void {
    const allContracts = [
      ...this.selectedContracts.value.contracts,
      ...this.exceptionsContracts.value.contracts
    ];

    const contract = allContracts.find(
      (c) => c.ContractId === contractId
    );

    if (contract) {
      // Atualiza o estado dos contratos selecionados
      this.changeSelectedContracts(contract);
    } else {
      console.warn(`Contrato com ID "${contractId}" não encontrado.`);
    }
  }

}
